import React from 'react'
import { Flex, Box, Text, Hide, Button } from 'pcln-design-system'
import PropTypes from 'prop-types'
import { TrustedAirlines } from './TrustedAirlines'
import { ExpressDealAncillaries } from './ExpressDealAncillaries'
import { ExpressDealVipInfo } from './ExpressDealVipInfo'
import { ExpressDealPrice } from './ExpressDealPrice'
import { ExpressDealSlices } from './ExpressDealSlices'
import { ExpressDealHeader } from './ExpressDealHeader'
import { ExpressDealDescription } from './ExpressDealDescription'
import { Wrapper } from './ExpressDealCard.style'
import SignInToSaveMore from './SignInToSaveMore'
import { DEFAULT_AIRLINES_DESCRIPTION } from './constants'

const ExpressDealCard = ({
  airlines,
  slices,
  headerText,
  vipSavingsPercent,
  promptSignIn,
  price,
  ancillaries,
  isPkg,
  budgetSaver,
  pricingDisclosure,
  onClick,
  vipTier,
  enableExpressDealVip,
  isFireFly,
  airlinesDescription,
  ...props
}) => {
  const signInPrompt = promptSignIn && enableExpressDealVip

  return (
    <Wrapper
      data-testid='pcln-exd-container'
      onClick={onClick}
      borderColor='primary.base'
      borderRadius='xl'
      {...props}
    >
      <ExpressDealHeader headerText={headerText} />
      <ExpressDealDescription budgetSaver={budgetSaver} mx={3} mt={3} />

      {signInPrompt && (
        <Hide md lg xl xxl>
          <Box mx={3} mt={3}>
            <SignInToSaveMore isFireFly={isFireFly} promptSignIn={promptSignIn} />
          </Box>
        </Hide>
      )}
      <Flex alignItems={'center'}>
        <Box width={[1, '72%']}>
          <TrustedAirlines airlines={airlines} ml={3} mt={3} mb={3} content={airlinesDescription} />
          <Hide xs sm>
            <ExpressDealSlices slices={slices} isPkg={isPkg} />
          </Hide>
          <Hide md lg xl xxl>
            <ExpressDealSlices slices={slices} isPkg={isPkg} />
          </Hide>
          {slices.length === 1 && ancillaries?.icons?.length && (
            <Box color='border.base' width={1} height='1px' mr={3} />
          )}
          <Hide xs sm>
            <ExpressDealAncillaries {...ancillaries} m={'24px 16px 16px'} />
          </Hide>
        </Box>
        <Text
          ml={'auto'}
          mt={0}
          py={[null, 3]}
          px={[3, 1]}
          width={['auto', '28%']}
          textAlign={['right', 'center']}
          alignSelf={[null, 'center']}
        >
          {!isPkg && !promptSignIn && enableExpressDealVip ? (
            <ExpressDealVipInfo
              mt={slices.length > 1 ? -1 : 3}
              vipSavingsPercent={vipSavingsPercent}
              vipTier={vipTier}
            />
          ) : null}
          <Hide xs sm>
            {signInPrompt && <SignInToSaveMore isFireFly={isFireFly} promptSignIn={promptSignIn} />}
          </Hide>
          <ExpressDealPrice
            pt={[2, '20px']}
            pb={3}
            price={price}
            isRoundTrip={slices.length > 1}
            pricingDisclosure={pricingDisclosure}
            color={'primary'}
          />
          <Hide xs sm>
            {isPkg && (
              <Button mb={2} color='success'>
                View Details
              </Button>
            )}
          </Hide>
        </Text>
      </Flex>
      <Hide md lg xl xxl>
        <ExpressDealAncillaries {...ancillaries} mx={3} my={2} />
      </Hide>
    </Wrapper>
  )
}

ExpressDealCard.defaultProps = {
  airlinesDescription: DEFAULT_AIRLINES_DESCRIPTION,
}

ExpressDealCard.propTypes = {
  /** Function when clicking on card */
  onClick: PropTypes.func,
  /** Function when clicking on sign in link (pass null if already signed in) */
  promptSignIn: PropTypes.func,
  /** Text in the top header */
  headerText: PropTypes.string,
  /** Percent savings with VIP discount */
  vipSavingsPercent: PropTypes.string,
  /** Is coming from the packages path */
  isPkg: PropTypes.bool,
  /** For packages, example: Flight + Car */
  pricingDisclosure: PropTypes.string,
  /** Included Ancillaries with description
   * icons: Array of Icons
   * description: description of all available ancillaries
   */
  ancillaries: PropTypes.shape({
    icons: PropTypes.array,
    description: PropTypes.string,
  }),
  /** Green Text at the top of the card.
   * longCopy: mobile only text
   * shortCopy: Desktop only text
   */
  budgetSaver: PropTypes.shape({
    longCopy: PropTypes.string,
    shortCopy: PropTypes.string,
    tag: PropTypes.string,
  }),
  /** Price of the express deal
   * packages uses incremental pricing
   */
  price: PropTypes.shape({
    dollars: PropTypes.string,
    cents: PropTypes.string,
    total: PropTypes.number,
    incrementalSign: PropTypes.string,
  }),
  /** List of trusted airlines */
  airlines: PropTypes.arrayOf(
    PropTypes.shape({
      logo: PropTypes.node,
      name: PropTypes.string,
    })
  ),
  /** Trusted irline description */
  airlinesDescription: PropTypes.string,
  /** Slice information for express deal */
  slices: PropTypes.arrayOf(
    PropTypes.shape({
      departure: PropTypes.string,
      arrival: PropTypes.string,
      takeoff: PropTypes.string,
      maximumStops: PropTypes.number,
      isOvernight: PropTypes.bool,
      displayDate: PropTypes.string,
      altOriginAirport: PropTypes.bool,
      altDestinationAirport: PropTypes.bool,
    })
  ),

  /** VIP tier level */
  vipTier: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  pclnExpt: PropTypes.func,

  enableExpressDealVip: PropTypes.bool,

  /* Denotes a Firefly search */
  isFireFly: PropTypes.bool,
}

export { ExpressDealCard }
